import { useState } from 'react';

import { KeyboardArrowDown } from '@mui/icons-material';
import { TimelineContent, TimelineItem, TimelineSeparator } from '@mui/lab';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Skeleton,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatDate } from 'utils/helpers';

import { useCareMatrix } from 'hooks/useCareMatrix';
import { getCareProgramExecution } from 'services/user';

import { Error, Topic } from 'components/CareMatrixModule/components';
import { EmptyState } from 'components/CareMatrixModule/components/EmptyState';

import * as S from './styles';

function CareMatrix() {
  const intl = useIntl();
  const { patientCognitoId } = useCareMatrix();
  const [programExpanded, setProgramExpanded] = useState<string | false>(false);
  const [topicExpanded, setTopicExpanded] = useState<string | false>(false);

  const { data, isLoading, isError } = useQuery({
    queryFn: () => getCareProgramExecution(patientCognitoId),
    queryKey: ['getCareProgramExecution', patientCognitoId],
  });

  const emptyCareProgram = data?.length === 0;

  function handleExpandProgramPanel(panel: string) {
    // eslint-disable-next-line func-names
    return function (_: React.SyntheticEvent, isExpanded: boolean) {
      setProgramExpanded(isExpanded ? panel : false);
    };
  }

  function handleExpandTopicPanel(panel: string) {
    // eslint-disable-next-line func-names
    return function (_: React.SyntheticEvent, isExpanded: boolean) {
      setTopicExpanded(isExpanded ? panel : false);
    };
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      pt={3}
      px={4}
      pb={4}
      bgcolor="background.paper"
    >
      {!isError ? (
        <>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            width="100%"
          >
            <Typography variant="h6">
              <FormattedMessage id="CAREMATRIXCPTC.PROGRAMCMTITLE.CAREPLAN" />
            </Typography>
            {isLoading ? (
              <Box width="100%" display="flex" flexDirection="column" gap={0.5}>
                <Skeleton height={24} width="100%" variant="text" />
                <Skeleton height={24} width="100%" variant="text" />
              </Box>
            ) : (
              <Typography variant="body1" color="text.secondary" mt={1.5}>
                <FormattedMessage
                  id="CAREMATRIXCPTC.PROGRAMCMSUBTITLE.CAREPLAN"
                  values={{
                    date:
                      (data &&
                        data?.length > 0 &&
                        formatDate(data[0].created_at, {
                          locale: intl.locale,
                        })) ||
                      '',
                  }}
                />
              </Typography>
            )}
          </Box>
          <Box mt={3} width="100%">
            {isLoading ? (
              <Box display="flex" flexDirection="column" width="100%" gap={1.5}>
                <Skeleton width="100%" variant="rectangular" height={72} />
                <Skeleton width="100%" variant="rectangular" height={72} />
              </Box>
            ) : (
              <S.Timeline>
                <>
                  {emptyCareProgram && (
                    <EmptyState
                      text={intl.formatMessage({
                        id: 'CAREMATRIXCPTC.EMPTYSTATES.NOTRECEIVED',
                      })}
                    />
                  )}

                  {data &&
                    data.length > 0 &&
                    data.map(program => {
                      const programIdentifier = `program-${program.id}`;
                      const isProgramExpanded =
                        programExpanded === programIdentifier;

                      return (
                        <TimelineItem key={program.id}>
                          <TimelineSeparator>
                            {program.status === 'FINISHED' ? (
                              <S.CheckCircle />
                            ) : (
                              <S.TimelineDot variant="outlined" />
                            )}
                            <S.TimelineConnector />
                          </TimelineSeparator>
                          <TimelineContent>
                            <Accordion
                              expanded={isProgramExpanded}
                              onChange={handleExpandProgramPanel(
                                programIdentifier,
                              )}
                            >
                              <AccordionSummary
                                expandIcon={<KeyboardArrowDown />}
                              >
                                <Typography>{program.title_key}</Typography>
                              </AccordionSummary>

                              <AccordionDetails>
                                {program.topics.map(topic => {
                                  const topicIdentifier = `program-${program.id}-topic-${topic.id}`;
                                  const isTopicExpanded =
                                    topicExpanded === topicIdentifier;

                                  return (
                                    <Accordion
                                      key={topicIdentifier}
                                      expanded={isTopicExpanded}
                                      onChange={handleExpandTopicPanel(
                                        topicIdentifier,
                                      )}
                                    >
                                      <AccordionSummary
                                        expandIcon={<KeyboardArrowDown />}
                                      >
                                        <Typography>
                                          {topic.title_key}
                                        </Typography>
                                      </AccordionSummary>
                                      <AccordionDetails>
                                        <Topic
                                          open={isTopicExpanded}
                                          cognitoId={patientCognitoId}
                                          topicId={topic.id}
                                        />
                                      </AccordionDetails>
                                    </Accordion>
                                  );
                                })}
                              </AccordionDetails>
                            </Accordion>
                          </TimelineContent>
                        </TimelineItem>
                      );
                    })}
                  {data && data.length > 0 && data[data.length - 1].has_next && (
                    <TimelineItem>
                      <TimelineSeparator>
                        <S.LockIcon />
                      </TimelineSeparator>
                      <TimelineContent width={370}>
                        <Accordion disabled>
                          <AccordionSummary expandIcon={<KeyboardArrowDown />}>
                            <Typography>
                              <FormattedMessage id="CAREMATRIXCPTC.PROGRAMCMBLOCK.LOADINGACTIVITIES" />
                            </Typography>
                          </AccordionSummary>
                        </Accordion>
                      </TimelineContent>
                    </TimelineItem>
                  )}
                </>
              </S.Timeline>
            )}
          </Box>
        </>
      ) : (
        <Error />
      )}
    </Box>
  );
}

export default CareMatrix;
